import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Error key stamped on an array control and on every repeated entry by `SvcValidators.uniqueValuesIn`. */
export const DUPLICATED_VALUE_ERROR = 'duplicated';

export class SvcValidators {
  static isEmptyInputValue(value: any): boolean {
    return value == null || value.length === 0;
  }

  /**
   * Rejects a `FormArray` whose entries repeat the value of `controlName`.
   *
   * Comparison ignores surrounding spaces and case, matching how the backends compare these
   * names. Empty values are never duplicates of each other, so freshly added rows stay valid
   * until the user types. The `duplicated` error is stamped both on the array — so the caller
   * can block a save with a single `hasError` check — and on every repeated entry, so the
   * inline validation message renders on the offending field.
   */
  static uniqueValuesIn(controlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const entries = control instanceof FormArray ? control.controls : [];
      const seenValues = new Set<string>();
      let hasDuplicates = false;

      entries.forEach((entry) => {
        const target = entry.get(controlName);
        if (!target) return;

        const normalized = SvcValidators.normalizeComparableValue(target.value);
        const isDuplicated = !!normalized && seenValues.has(normalized);
        if (normalized) {
          seenValues.add(normalized);
        }

        if (isDuplicated) {
          hasDuplicates = true;
        }

        SvcValidators.applyDuplicatedError(target, isDuplicated);
      });

      return hasDuplicates ? { [DUPLICATED_VALUE_ERROR]: true } : null;
    };
  }

  /** Values are compared without surrounding spaces and case-insensitively. */
  static normalizeComparableValue(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  /** Adds or removes the duplicated error while preserving the control's own errors. */
  private static applyDuplicatedError(control: AbstractControl, isDuplicated: boolean): void {
    const errors = { ...control.errors };

    if (isDuplicated) {
      control.setErrors({ ...errors, [DUPLICATED_VALUE_ERROR]: true });
      return;
    }

    if (errors[DUPLICATED_VALUE_ERROR]) {
      delete errors[DUPLICATED_VALUE_ERROR];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  static spaceNotAllowed = (control: AbstractControl): ValidationErrors | null => {
    if (/ /g.test(control.value ?? '')) {
      return { spaceNotAllowed: true };
    }

    return null;
  };

  static minDecimalPlaces(length: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!control.value) return null;

      const value = control.value.toString();
      const decimalPart = value.split('.')[1];

      if (!decimalPart || decimalPart.length < 6) {
        return { invalidDecimalPlaces: true };
      }

      return null;
    };
  }

  static mustMatch(controlPath: string, matchingControlPath: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      // Get the control and matching control
      const control = formGroup.get(controlPath);
      const matchingControl = formGroup.get(matchingControlPath);

      // Return if control or matching control doesn't exist
      if (!control || !matchingControl) {
        return null;
      }

      // Delete the mustMatch error to reset the error on the matching control
      if (matchingControl.hasError('mustMatch')) {
        delete matchingControl.errors.mustMatch;
        matchingControl.updateValueAndValidity();
      }

      // Don't validate empty values on the matching control
      // Don't validate if values are matching
      if (this.isEmptyInputValue(matchingControl.value) || control.value === matchingControl.value) {
        return null;
      }

      // Prepare the validation errors
      const errors = { mustMatch: true };

      // Set the validation error on the matching control
      matchingControl.setErrors(errors);

      // Return the errors
      return errors;
    };
  }

  static maxLengthHtml(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.isEmptyInputValue(control.value)) {
        return null;
      }

      const plainText = control.value
        .replace(/<[^>]*>/g, '') 
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .trim();

      return plainText.length > maxLength ? { maxlength: { requiredLength: maxLength, actualLength: plainText.length } } : null;
    };
  }
}
